//デザインタブ-入力項目選択メニュー (template/common 共通)
import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Button } from '@material-ui/core'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import InputIcon from '@material-ui/icons/InputOutlined'
import CategoryOutlinedIcon from '@material-ui/icons/CategoryOutlined';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import CropSquareIcon from '@material-ui/icons/CropSquare';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import ImageIcon from '@material-ui/icons/ImageOutlined';
import { useDispatch } from 'react-redux';

import useLatelyImages from '../hooks/useLatelyImages';
import { generateImageId, addImage } from '../redux/imageDbSlice';
import { AttributeKinds } from '../redux/cardAttributeModelIndex';
import useCurrentUserId from '../hooks/useCurrentUserId';
import { FullSizeImageAddType, updateImageDbs } from '../general';

const ItemMenuWrapper = styled.div`
  overflow-x: hidden;
`
const MenuButton = styled(Button)`
  size: 'large';
  width: 100%;
  margin-left: 5px;
  margin-bottom: 20px;
`
const ReversedChatBubbleOutlineIcon = styled(ChatBubbleOutlineIcon)`
  transform: scaleX(-1);
`

type ImageItemStandbyType = 'attribute' | 'shape'

//デザインタブ-入力項目選択メニュー
type ItemMenuProps = {
  cardKind: string;
  onAddAttribute: (kind: AttributeKinds, image?: FullSizeImageAddType) => void;
  onAddShape: (kind: string, image?: FullSizeImageAddType) => void;
  onImageBrowser: (type: ImageItemStandbyType) => void;
  onDrawTool?: (type: ImageItemStandbyType) => void;
}
const ItemMenu: React.FC<ItemMenuProps> = ({cardKind, onAddAttribute, onAddShape, onImageBrowser, onDrawTool}) => {
  const dispatch = useDispatch();
  const userId = useCurrentUserId();
  const [imageAttributeAnchorEl, setImageAttributeAnchorEl] = useState<null | HTMLElement>(null);
  const [inputAnchorEl, setInputAnchorEl] = useState<null | HTMLElement>(null);
  const [shapeAnchorEl, setShapeAnchorEl] = useState<null | HTMLElement>(null);
  const [balloonAnchorEl, setBalloonAnchorEl] = useState<null | HTMLElement>(null);
  const [imageShapeAnchorEl, setImageShapeAnchorEl] = useState<null | HTMLElement>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | undefined>(undefined);
  const [imageItemStandby, setImageItemStandby] = useState<ImageItemStandbyType | undefined>(undefined);
  const [latelyIds, , latelyImagesId] = useLatelyImages();

  useEffect(() => {
    if (!imgElement || !imageItemStandby) return;
    const width = imgElement.width;
    const height = imgElement.height;
    if (width === 0 && height === 0) return;
    const imageId = generateImageId();
    updateImageDbs({ dispatch, imageId, dataUrlBase64: imgElement.src, userId, latelyIds, latelyImagesId });
    if (imageItemStandby === 'attribute') onAddAttribute('image', { id: imageId, width, height });
    else onAddShape('image', { id: imageId, width, height });
    setImageItemStandby(undefined);
    setImgElement(undefined);
  }, [imgElement, imageItemStandby])

  const inputSingleTextHandle = () => {
    onAddAttribute('single');
    setInputAnchorEl(null);
  }
  const inputMultiTextHandle = () => {
    onAddAttribute('multi');
    setInputAnchorEl(null);
  }
  const inputCheckHandle = () => {
    onAddAttribute('check');
    setInputAnchorEl(null);
  } 
  const inputTableHandle = () => {
    onAddAttribute('table');
    setInputAnchorEl(null);
  } 
  const inputNondisplayHandle = () => {
    onAddAttribute('nondisplay');
    setInputAnchorEl(null);
  }
  const shapeTextHandle = () => {
    onAddShape('text');
    setShapeAnchorEl(null);
  }
  const shapeCircleHandle = () => {
    onAddShape('circle');
    setShapeAnchorEl(null);
  }
  const shapeRectangleHandle = () => {
    onAddShape('rectangle');
    setShapeAnchorEl(null);
  }
  const shapeBalloonHandle = () => {
    onAddShape('balloon');
    setBalloonAnchorEl(null);
    setShapeAnchorEl(null);
  }
  const shapeReverseBalloonHandle = () => {
    onAddShape('reverseBalloon');
    setBalloonAnchorEl(null);
    setShapeAnchorEl(null);
  }
  const inputHandleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setInputAnchorEl(event.currentTarget)
  }
  const inputHandleClose = () => {
    setInputAnchorEl(null)
  }
  const shapeHandleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShapeAnchorEl(event.currentTarget);
  }
  const shapeHandleClose = () => {
    setShapeAnchorEl(null);
  }
  const balloonHandleClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    setBalloonAnchorEl(event.currentTarget);
  }
  const balloonHandleClose = () => {
    setBalloonAnchorEl(null);
  }
  const imageAttributeHandleClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    setImageAttributeAnchorEl(event.currentTarget);
  }
  const imageAttributeHandleClose = () => {
    setImageAttributeAnchorEl(null);
    setInputAnchorEl(null);
  }
  const imageShapeHandleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setImageShapeAnchorEl(event.currentTarget);
  }
  const imageShapeHandleClose = () => {
    setImageShapeAnchorEl(null);
    setInputAnchorEl(null);
  }
  const imageBrowserOpen = (type: ImageItemStandbyType) => {
    imageAttributeHandleClose();
    imageShapeHandleClose();
    onImageBrowser(type);
  }
  const drawToolOpen = (type: ImageItemStandbyType) => {
    imageAttributeHandleClose();
    imageShapeHandleClose();
    if (onDrawTool) onDrawTool(type);
  }
  const openUserFileSelector = (htmlId: string) => {
    imageAttributeHandleClose();
    imageShapeHandleClose();
    const selector = document.getElementById(htmlId);
    if (!selector) return;
    selector.click();
  }
  const getFile = (ev: React.ChangeEvent<HTMLInputElement>, type: ImageItemStandbyType) => {
    if (!ev.target.files) return;
    const file = ev.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      const dataUrlBase64 = reader.result;
      const img = new Image();
      img.src = dataUrlBase64;
      img.style.display = 'none';
      img.remove();

      setImageItemStandby(type);
      setImgElement(img);
    }
    ev.currentTarget.value = '';
  }

  return (
    <ItemMenuWrapper>
      <MenuButton aria-controls="input-menu" aria-haspopup="true" onClick={inputHandleClick}>
        <InputIcon /><pre> 入力</pre>
      </MenuButton>
      <Menu id="input-menu" anchorEl={inputAnchorEl} keepMounted
        open={Boolean(inputAnchorEl)}
        onClose={inputHandleClose}
      >
        <MenuItem onClick={inputSingleTextHandle}>単一行型</MenuItem>
        <MenuItem onClick={inputMultiTextHandle}>複数行型</MenuItem>
        {/* <MenuItem onClick={inputTableHandle}>表型</MenuItem> */}
        {/* <MenuItem onClick={inputCheckHandle}>チェック型</MenuItem> */}
        <MenuItem aria-controls="image-attribute-menu" aria-haspopup="true" onClick={imageAttributeHandleClick}>
          画像型
        </MenuItem>
        <Menu id="image-attribute-menu" anchorEl={imageAttributeAnchorEl} keepMounted
          open={Boolean(imageAttributeAnchorEl)}
          onClose={imageAttributeHandleClose}
        >
          <MenuItem onClick={() => imageBrowserOpen('attribute')}>画像ブラウザ...</MenuItem>
          <MenuItem onClick={() => openUserFileSelector('user-file-selector-attribute')} id="user-file-selector-button-attribute">
            <input type="file" accept="image/png, image/jpeg" id="user-file-selector-attribute" 
                   onChange={(ev) => getFile(ev, 'attribute')} style={{display: 'none'}} />ファイルから挿入...
          </MenuItem>
          <MenuItem onClick={() => drawToolOpen('attribute')}>絵を描いて挿入...</MenuItem>
        </Menu>
        <MenuItem onClick={inputNondisplayHandle}>非表示型</MenuItem>
      </Menu>
      {/* <MenuButton aria-controls="shape-menu" aria-haspopup="true" onClick={shapeHandleClick}>
        <CategoryOutlinedIcon />文字図形
      </MenuButton>
      <Menu id="shape-menu" anchorEl={shapeAnchorEl} keepMounted
        open={Boolean(shapeAnchorEl)}
        onClose={shapeHandleClose}
      >
        <MenuItem onClick={shapeTextHandle}><TextFieldsIcon />固定文字</MenuItem>
        <MenuItem onClick={shapeCircleHandle}><RadioButtonUncheckedIcon />円</MenuItem>
        <MenuItem onClick={shapeRectangleHandle}><CropSquareIcon />四角</MenuItem>
        <MenuItem aria-controls="balloon-menu" aria-haspopup="true" onClick={balloonHandleClick}>
          <ChatBubbleOutlineIcon />ふきだし ▷
        </MenuItem>
        <Menu id="balloon-menu" anchorEl={balloonAnchorEl} keepMounted
          open={Boolean(balloonAnchorEl)}
          onClose={balloonHandleClose}
        >
          <MenuItem onClick={shapeBalloonHandle}><ChatBubbleOutlineIcon />ふきだし</MenuItem>
          <MenuItem onClick={shapeReverseBalloonHandle}><ReversedChatBubbleOutlineIcon />ふきだし</MenuItem>
        </Menu>
      </Menu> */}
      <MenuButton onClick={shapeTextHandle}><TextFieldsIcon />固定文字</MenuButton>
      <MenuButton aria-controls="image-shape-menu" aria-haspopup="true" onClick={imageShapeHandleClick}>
        <ImageIcon />固定画像
      </MenuButton>
      <Menu id="image-shape-menu" anchorEl={imageShapeAnchorEl} keepMounted
        open={Boolean(imageShapeAnchorEl)}
        onClose={imageShapeHandleClose}
      >
        <MenuItem onClick={() => imageBrowserOpen('shape')}>画像ブラウザ...</MenuItem>
        <MenuItem onClick={() => openUserFileSelector('user-file-selector-shape')} id="user-file-selector-button-shape">
          <input type="file" accept="image/png, image/jpeg" id="user-file-selector-shape" onChange={(ev) => getFile(ev, 'shape')} style={{display: 'none'}} />ファイルから挿入...
        </MenuItem>
        <MenuItem onClick={() => drawToolOpen('shape')}>絵を描いて挿入...</MenuItem>
      </Menu>
    </ItemMenuWrapper>
  )
}

export default ItemMenu;
